import { Plan } from '@/src/components/desktop/srp/fareFamily/types'
import { Fare, FlightCard } from '@/src/types/flight'
import { constructSplrt } from './constructSplRT'

/**
 * Fare-family "Regular" behaves like "no plan selected".
 * This helper is intentionally shared so `buildItineraryPayload` and `useFlightDetails`
 * use the exact same branching rules.
 */
export function isRegularFareFamilyPlan (plan: Plan | null | undefined): boolean {
    return !plan || plan.ctBrandName === 'Regular'
}

export type SplrtJourneyContext = {
    isSplrtJourney: boolean
    onwardSFare?: Fare
    returnSFare?: Fare
    onwardSolutionId?: string
    returnSolutionId?: string
    onwardSplrtDfd?: string
    returnSplrtDfd?: string
}

/**
 * Computes SPLRT applicability + payload inputs for an RT journey.
 *
 * SPLRT is only used when:
 * - Both fare-family plans are "Regular" (or missing), AND
 * - Both legs are marked special RT by `constructSplrt`, AND
 * - Total SPLRT price is > 0 and <= total retail price (safety check used across the app)
 */
export function getSplrtJourneyContextForRT ({
    onwardCard,
    returnCard,
    onwardPlan,
    returnPlan
}: {
    onwardCard: FlightCard
    returnCard: FlightCard
    onwardPlan?: Plan | null
    returnPlan?: Plan | null
}): SplrtJourneyContext {
    const shouldConsiderSplrt = isRegularFareFamilyPlan(onwardPlan) && isRegularFareFamilyPlan(returnPlan)

    if (!shouldConsiderSplrt) {
        return { isSplrtJourney: false }
    }

    const onwardSplrt = constructSplrt(onwardCard, returnCard?.splRtFn)
    const returnSplrt = constructSplrt(returnCard, onwardCard?.splRtFn)

    let isSplrtJourney = !!(onwardSplrt?.isSpecialRT && returnSplrt?.isSpecialRT)
    if (!isSplrtJourney) {
        return { isSplrtJourney: false }
    }

    // Price validation: SPLRT should not exceed retail combined price.
    const totalRetailPrice = (onwardCard?.priceBreakup?.fare?.pr || 0) + (returnCard?.priceBreakup?.fare?.pr || 0)
    const totalSplrtPrice = ((onwardSplrt?.sFare as Fare | undefined)?.pr || 0) + ((returnSplrt?.sFare as Fare | undefined)?.pr || 0)
    isSplrtJourney = totalSplrtPrice > 0 && totalSplrtPrice <= totalRetailPrice

    if (!isSplrtJourney) {
        return { isSplrtJourney: false }
    }

    return {
        isSplrtJourney: true,
        onwardSFare: onwardSplrt?.sFare as Fare | undefined,
        returnSFare: returnSplrt?.sFare as Fare | undefined,
        onwardSolutionId: onwardSplrt?.solutionId,
        returnSolutionId: returnSplrt?.solutionId,
        onwardSplrtDfd: onwardSplrt?.splrtDfd,
        returnSplrtDfd: returnSplrt?.splrtDfd
    }
}
